import React from "react";
import { Card, Row, Col, Table, Badge, ProgressBar } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAnalytics } from "../hooks/useAnalytics";
import { STATS_COLORS } from "../utils/helpers";

const ModelComparisonPage: React.FC = () => {
  const { analyticsData, isLoading, error } = useAnalytics(0);

  const processedData = React.useMemo(() => {
    if (!analyticsData) return null;

    return analyticsData.model_comparison.map((model) => ({
      name: model.model_name,
      jobs: model.total_jobs,
      redactions: model.total_redactions,
      avgTime: model.average_processing_time,
      avgConfidence: model.average_confidence,
      rps: model.redactions_per_second,
      color: model.model_name.includes("ensemble")
        ? STATS_COLORS.purple
        : model.model_name.includes("bert")
        ? STATS_COLORS.primary
        : STATS_COLORS.secondary,
    }));
  }, [analyticsData]);

  const formatTime = (seconds: number) => {
    if (seconds < 1) return `${(seconds * 1000).toFixed(0)}ms`;
    return `${seconds.toFixed(2)}s`;
  };

  const getPerformanceStatus = (rps: number) => {
    if (rps >= 50) return { status: "Excellent", color: "success" };
    if (rps >= 30) return { status: "Good", color: "info" };
    if (rps >= 15) return { status: "Fair", color: "warning" };
    return { status: "Slow", color: "danger" };
  };

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!processedData) return <div>No data available.</div>;

  return (
    <>
      <h2 className="my-4">Model Performance Comparison</h2>
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>Performance Overview</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={processedData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar
                    dataKey="rps"
                    fill={STATS_COLORS.accent}
                    name="Redactions per Second"
                  />
                  <Bar
                    dataKey="avgConfidence"
                    fill={STATS_COLORS.info}
                    name="Average Confidence"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Header>Detailed Stats</Card.Header>
            <Card.Body>
              <Table striped bordered hover responsive>
                <thead>
                  <tr>
                    <th>Model</th>
                    <th>Jobs</th>
                    <th>Total Redactions</th>
                    <th>Avg Time / Job</th>
                    <th>Avg Confidence</th>
                    <th>Redactions / Sec</th>
                    <th>Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {processedData.map((model, index) => {
                    const performance = getPerformanceStatus(model.rps);
                    return (
                      <tr key={index}>
                        <td>{model.name}</td>
                        <td>{model.jobs}</td>
                        <td>{model.redactions.toLocaleString()}</td>
                        <td>{formatTime(model.avgTime)}</td>
                        <td>
                          <ProgressBar
                            now={model.avgConfidence * 100}
                            label={`${(model.avgConfidence * 100).toFixed(1)}%`}
                            variant={
                              model.avgConfidence > 0.9
                                ? "success"
                                : model.avgConfidence > 0.75
                                ? "warning"
                                : "danger"
                            }
                          />
                        </td>
                        <td>{model.rps.toFixed(2)}</td>
                        <td>
                          <Badge bg={performance.color}>
                            {performance.status}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default ModelComparisonPage;
